import { useGetOne } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowLeft,
  Briefcase,
  Calendar,
  Loader2,
  ChevronRight,
} from "lucide-react";
import { JobStatusBadge } from "./JobStatusBadge";
import { JobMarginCard } from "./JobMarginCard";
import { JobExpenses } from "./JobExpenses";
import { useJobActions } from "./useJobActions";
import { JobAssignmentList } from "../subs/JobAssignmentList";
import { DailyReportList } from "../fieldops/DailyReportList";
import { ChangeOrderList } from "../fieldops/ChangeOrderList";
import { AuditTrailViewer } from "../fieldops/AuditTrailViewer";
import { JobScheduleCard } from "../fieldops/JobScheduleCard";
import { JobCompletionPanel } from "../closing/JobCompletionPanel";

interface Job {
  id: string;
  org_id: string;
  contact_id: string | number;
  estimate_id: string | null;
  job_number: string;
  status: string;
  title: string;
  description: string;
  notes: string;
  estimated_total: number;
  estimated_labor: number;
  estimated_material: number;
  actual_labor: number;
  actual_material: number;
  actual_subcontractor: number;
  actual_other: number;
  actual_total: number;
  margin: number;
  margin_pct: number;
  scheduled_start: string | null;
  scheduled_end: string | null;
  actual_start: string | null;
  actual_end: string | null;
  completed_at: string | null;
  closed_at: string | null;
  created_at: string;
  updated_at: string;
}

interface JobDetailProps {
  jobId: string;
  onBack: () => void;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const STATUS_ACTIONS: Record<
  string,
  { label: string; to: string; color: string }[]
> = {
  created: [
    { label: "Schedule", to: "scheduled", color: "#2563eb" },
    { label: "Start Now", to: "in_progress", color: "#d97706" },
  ],
  scheduled: [
    { label: "Start Work", to: "in_progress", color: "#d97706" },
  ],
  in_progress: [
    { label: "Mark Complete", to: "completed", color: "#16a34a" },
  ],
  completed: [
    { label: "Close Job", to: "closed", color: "#6b7280" },
  ],
  closed: [],
};

export function JobDetail({ jobId, onBack }: JobDetailProps) {
  const { data: job, isPending } = useGetOne<Job>("jobs", { id: jobId });
  const { transition, isPending: actionPending } = useJobActions();

  if (isPending || !job) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        Loading job…
      </div>
    );
  }

  const actions = STATUS_ACTIONS[job.status] || [];
  const isClosed = job.status === "closed";

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-1.5 rounded hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-mono text-muted-foreground">
              {job.job_number}
            </span>
            <JobStatusBadge status={job.status} />
          </div>
          <h3 className="text-lg font-semibold truncate mt-0.5">
            {job.title || "Untitled Job"}
          </h3>
        </div>

        {/* Status actions */}
        <div className="flex gap-2 shrink-0">
          {actions.map((action) => (
            <button
              key={action.to}
              onClick={() => transition(job, action.to)}
              disabled={actionPending}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50"
              style={{ backgroundColor: action.color }}
            >
              {actionPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5" />
              )}
              {action.label}
            </button>
          ))}
        </div>
      </div>

      {/* Description */}
      {job.description && (
        <Card>
          <CardContent className="p-4">
            <p className="text-sm">{job.description}</p>
          </CardContent>
        </Card>
      )}

      {/* Metadata */}
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          Created {formatDate(job.created_at)}
        </span>
        {job.scheduled_start && (
          <span>Scheduled: {formatDate(job.scheduled_start)}</span>
        )}
        {job.actual_start && (
          <span>Started: {formatDate(job.actual_start)}</span>
        )}
        {job.actual_end && (
          <span>Ended: {formatDate(job.actual_end)}</span>
        )}
        {job.estimate_id && (
          <span className="font-mono">
            From estimate
          </span>
        )}
      </div>

      {/* Margin card */}
      <JobMarginCard
        estimatedTotal={Number(job.estimated_total)}
        actualTotal={Number(job.actual_total)}
        margin={Number(job.margin)}
        marginPct={Number(job.margin_pct)}
        estimatedLabor={Number(job.estimated_labor)}
        estimatedMaterial={Number(job.estimated_material)}
        actualLabor={Number(job.actual_labor)}
        actualMaterial={Number(job.actual_material)}
        actualSubcontractor={Number(job.actual_subcontractor)}
        actualOther={Number(job.actual_other)}
      />

      {/* Subcontractor assignments */}
      <JobAssignmentList jobId={job.id} contactId={job.contact_id} readOnly={isClosed} />

      {/* Expenses */}
      <JobExpenses jobId={job.id} readOnly={isClosed} />

      {/* Daily Reports */}
      <DailyReportList jobId={job.id} contactId={job.contact_id} readOnly={isClosed} />

      {/* Change Orders */}
      <ChangeOrderList jobId={job.id} contactId={job.contact_id}
        currentTotal={Number(job.estimated_total)} readOnly={isClosed} />

      {/* Audit Trail */}
      <AuditTrailViewer jobId={job.id} />

      {/* Completion Checklist */}
      <JobCompletionPanel
        jobId={job.id}
        contactId={job.contact_id}
        contactName={""}
        jobStatus={job.status}
        estimatedTotal={Number(job.estimated_total)}
      />
    </div>
  );
}
